// =====================================================
// MODAL: CreateTaskModal
// Description: Modale complète pour créer une tâche (selon Figma)
// =====================================================

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { User, ChevronDown } from 'lucide-react';
import { createTaskSchema, type CreateTaskInput } from '@/schemas/task';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormInput, FormSelect, FormTextarea } from '../ui';
import { AssignUserModal } from './AssignUserModal';
import type { EntityType } from '@/types/timeline';
import { useSupabase } from '@/hooks/useSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useUsers } from '@/contexts/UsersContext';
import { usePermissions } from '@/hooks/usePermissions';
import { getEntityDisplayName } from '@/lib/timeline-logger';
import { toast } from 'sonner';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  onSuccess?: () => void;
}

const TASK_TYPE_OPTIONS = [
  { value: 'call', label: 'Appel' },
  { value: 'email', label: 'Mail' },
  { value: 'meeting', label: 'Réunion' },
  { value: 'other', label: 'Autres' },
];

export function CreateTaskModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  onSuccess,
}: CreateTaskModalProps) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const { users } = useUsers();
  const { canManageAssignments } = usePermissions();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  const form = useForm<CreateTaskInput>({
    resolver: zodResolver(createTaskSchema),
    defaultValues: {
      title: '',
      type: 'call',
      date: '',
      time: '',
      // Retour réunion 07/07 (tâche-dashboard) : par défaut, la tâche est
      // assignée à SON CRÉATEUR. Avant, `assigned_to` valait `null` et, pour un
      // admin/superadmin (`canManageAssignments` = true), la création
      // conservait ce null → la tâche n'était assignée à personne et
      // n'apparaissait pas dans « Mes tâches ». La requête / le filtre
      // `assigned_to = userId` sont corrects (vérifiés en base) : le bug était
      // l'assignation par défaut. L'utilisateur peut toujours réassigner.
      assigned_to: user?.id ?? null,
      notes: '',
    },
  });

  // `user` peut être null au tout premier rendu (auth en cours) : re-défaut à soi
  // à l'ouverture de la modale si aucune assignation n'a encore été choisie, pour
  // garantir qu'une tâche créée est self-assignée par défaut (cf. retour 07/07).
  useEffect(() => {
    if (isOpen && user?.id && !form.getValues('assigned_to')) {
      form.setValue('assigned_to', user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id]);

  // Trouver l'utilisateur sélectionné
  const selectedUser = users.find((u) => u.id === form.watch('assigned_to'));

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: CreateTaskInput) => {
    try {
      // Construire la date complète EN HEURE LOCALE
      let dueDate: string;
      if (data.time) {
        // Créer une date avec l'heure locale saisie
        const [hours, minutes] = data.time.split(':').map(Number);
        const localDate = new Date(data.date);
        localDate.setHours(hours, minutes, 0, 0);
        // Convertir en ISO (inclut le fuseau horaire)
        dueDate = localDate.toISOString();
      } else {
        // Heure par défaut à midi en heure locale
        const localDate = new Date(data.date);
        localDate.setHours(12, 0, 0, 0);
        dueDate = localDate.toISOString();
      }

      // Créer la tâche
      // Si l'utilisateur ne peut pas gérer les assignations (role 'user'), auto-assigner à lui-même
      const assignedTo = canManageAssignments() ? data.assigned_to : user?.id || null;

      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .insert({
          entity_type: entityType,
          entity_id: entityId,
          type: data.type,
          title: data.title,
          notes: data.notes || null,
          due_date: dueDate,
          completed: false,
          assigned_to: assignedTo,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // ✅ Récupérer le nom de l'entité pour un contexte clair
      const entityName = await getEntityDisplayName(supabase, entityType, entityId);
      const titlePrefix = entityName ? `${entityName} - ` : '';

      // Logger dans la timeline
      await supabase.from('timeline_events').insert({
        entity_type: entityType,
        entity_id: entityId,
        event_type: 'task_created',
        title: `${titlePrefix}Tâche créée : ${data.title}`,
        description: `Type: ${TASK_TYPE_OPTIONS.find((t) => t.value === data.type)?.label}`,
        created_by: user?.id || null,
        metadata: {
          task_id: taskData.id,
          task_type: data.type,
          due_date: dueDate,
          title: data.title,
          entity_name: entityName,
        },
      });

      toast.success('Tâche créée', {
        description: "La tâche a été ajoutée à l'historique",
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error('Erreur lors de la création de la tâche:', error);
      toast.error('Erreur', {
        description: 'Impossible de créer la tâche',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nouvelle tâche"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-task-form"
        />
      }
    >
      <Form {...form}>
        <form id="create-task-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            {/* Titre */}
            <FormInput
              control={form.control as any}
              name="title"
              label="Titre"
              placeholder="Titre de la tâche"
              required
            />

            {/* Type */}
            <FormSelect
              control={form.control as any}
              name="type"
              label="Type"
              required
              options={TASK_TYPE_OPTIONS}
            />

            {/* Date et Heure */}
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                control={form.control as any}
                name="date"
                label="Date"
                type="date"
                required
              />
              <FormInput
                control={form.control as any}
                name="time"
                label="Heure (optionnel)"
                type="time"
                placeholder="09:00"
              />
            </div>

            {/* Assigné à - Visible uniquement pour admin et superadmin */}
            {canManageAssignments() && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Assigné à</label>
                <button
                  type="button"
                  onClick={() => setIsUserModalOpen(true)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm border border-zinc-300 rounded-md bg-white hover:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <User className="size-4 text-zinc-400" />
                    <span className={selectedUser ? 'text-zinc-900' : 'text-zinc-500'}>
                      {selectedUser ? selectedUser.full_name || selectedUser.email : 'Non assigné'}
                    </span>
                  </div>
                  <ChevronDown className="size-4 text-zinc-400" />
                </button>
              </div>
            )}

            {/* Description */}
            <FormTextarea
              control={form.control as any}
              name="notes"
              label="Description (optionnel)"
              placeholder="Description de la tâche..."
              rows={3}
            />
          </div>
        </form>
      </Form>

      {/* Modale d'assignation d'utilisateur */}
      <AssignUserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        currentUserId={form.watch('assigned_to')}
        onAssign={async (userId) => {
          form.setValue('assigned_to', userId);
        }}
        onUnassign={async () => {
          form.setValue('assigned_to', null);
        }}
        showUnassignOption={true}
        title="Assigner la tâche à"
      />
    </Modal>
  );
}
